import React, { useState } from "react";
import { useAuth } from "../auth/AuthContext";
import Select from "./Select";

const VALID_STATUSES = ["active", "inactive"];
const VALID_REGISTERS = ["active", "inactive"];
const VALID_TYPES = ["Mini", "Normal"];

export default function CreateScenathon({ onScenathonCreated, standalone = true }) {
  const { authFetch } = useAuth();
  const [formData, setFormData] = useState({
    name: "",
    notification_email: "",
    status: "active",
    allow_trade: false,
    year: new Date().getFullYear(),
    register: "active",
    type: "Mini",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Default Pathways
  const [defaultPathways, setDefaultPathways] = useState([]);
  const [newPathway, setNewPathway] = useState({
    defaultname: "",
    deadline: "",
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleAddPathway = () => {
    if (!newPathway.defaultname.trim()) {
      setError("Pathway name is required");
      return;
    }
    if (!newPathway.deadline) {
      setError("Deadline is required");
      return;
    }

    setDefaultPathways((prev) => [
      ...prev,
      {
        id: Date.now(),
        defaultname: newPathway.defaultname.trim(),
        deadline: newPathway.deadline,
      },
    ]);

    setNewPathway({
      defaultname: "",
      deadline: "",
    });
    setError(null);
  };

  const handleRemovePathway = (id) => {
    setDefaultPathways((prev) => prev.filter((p) => p.id !== id));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Validation
    if (!formData.name.trim()) {
      setError("Scenathon name is required");
      return;
    }

    try {
      setLoading(true);

      const response = await authFetch("/api/scenathon/scenathons", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.name.trim(),
          notification_email: formData.notification_email.trim() || undefined,
          status: formData.status,
          allow_trade: formData.allow_trade,
          year: formData.year ? Number(formData.year) : undefined,
          register: formData.register,
          type: formData.type.trim() || undefined,
          defaultPathways: defaultPathways.map((p) => ({
            defaultname: p.defaultname,
            deadline: p.deadline,
          })),
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Error creating scenathon");
      }

      const data = await response.json();
      setSuccess(`✓ Scenathon "${data.scenathon.name}" created successfully!`);

      // Reset form
      setFormData({
        name: "",
        notification_email: "",
        status: "active",
        allow_trade: false,
        year: new Date().getFullYear(),
        register: "active",
        type: "Mini",
      });
      
      // Reset pathways
      setDefaultPathways([]);
      setNewPathway({
        defaultname: "",
        deadline: "",
      });

      // Call callback if provided
      if (onScenathonCreated) {
        onScenathonCreated(data.scenathon);
      }
    } catch (err) {
      console.error("Create scenathon error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`w-full p-4 md:p-6 font-montserrat ${standalone ? "max-w-7xl mx-auto" : ""}`}>
      {/* Header */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Create New Scenathon</h2>
        <p className="text-gray-600">
          Add a new scenathon to the system. Only administrators can create scenathons.
        </p>
      </div>

        {/* Success Message */}
        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-700">{success}</p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm p-6 space-y-6">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Scenathon Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter scenathon name"
              disabled={loading}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notification Email
            </label>
            <input
              type="email"
              name="notification_email"
              value={formData.notification_email}
              onChange={handleChange}
              placeholder="admin@scenathon.org"
              disabled={loading}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
            />
          </div>

          {/* Year */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Year
            </label>
            <input
              type="number"
              name="year"
              value={formData.year}
              onChange={handleChange}
              min="1900"
              max="2100"
              disabled={loading}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
            />
          </div>

          {/* Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type
            </label>
            <Select
              value={formData.type}
              onChange={(value) =>
                setFormData((prev) => ({ ...prev, type: value }))
              }
              options={VALID_TYPES.map((t) => ({ value: t, label: t }))}
              disabled={loading}
              placeholder="Select type"
            />
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <Select
              value={formData.status}
              onChange={(value) =>
                setFormData((prev) => ({ ...prev, status: value }))
              }
              options={VALID_STATUSES.map((s) => ({ value: s, label: s.charAt(0).toUpperCase() + s.slice(1) }))}
              disabled={loading}
              placeholder="Select status"
            />
          </div>

          {/* Register */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Registration Status
            </label>
            <Select
              value={formData.register}
              onChange={(value) =>
                setFormData((prev) => ({ ...prev, register: value }))
              }
              options={VALID_REGISTERS.map((r) => ({ value: r, label: r.charAt(0).toUpperCase() + r.slice(1) }))}
              disabled={loading}
              placeholder="Select registration status"
            />
          </div>

          {/* Allow Trade */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="allow_trade"
              name="allow_trade"
              checked={formData.allow_trade}
              onChange={handleChange}
              disabled={loading}
              className="w-4 h-4 text-green-600 rounded focus:ring-2 focus:ring-green-500 disabled:opacity-50"
            />
            <label htmlFor="allow_trade" className="ml-3 text-sm font-medium text-gray-700">
              Allow Trade
            </label>
          </div>

          {/* Default Pathways Section */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Default Pathways</h3>
            
            {/* Add Pathway Form */}
            <div className="space-y-3 mb-4 p-4 bg-gray-50 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pathway Name
                </label>
                <input
                  type="text"
                  value={newPathway.defaultname}
                  onChange={(e) =>
                    setNewPathway((prev) => ({
                      ...prev,
                      defaultname: e.target.value,
                    }))
                  }
                  placeholder="Enter pathway name"
                  disabled={loading}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Deadline
                </label>
                <input
                  type="date"
                  value={newPathway.deadline}
                  onChange={(e) =>
                    setNewPathway((prev) => ({
                      ...prev,
                      deadline: e.target.value,
                    }))
                  }
                  disabled={loading}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
                />
              </div>

              <button
                type="button"
                onClick={handleAddPathway}
                disabled={loading}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
              >
                Add Pathway
              </button>
            </div>

            {/* Pathways Table */}
            {defaultPathways.length > 0 && (
              <div className="overflow-x-auto mb-4">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100 border-b border-gray-300">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium text-gray-700">Pathway Name</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-700">Deadline</th>
                      <th className="px-4 py-2 text-center font-medium text-gray-700">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {defaultPathways.map((pathway) => (
                      <tr key={pathway.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-gray-900">{pathway.defaultname}</td>
                        <td className="px-4 py-2 text-gray-900">
                          {new Date(pathway.deadline).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-2 text-center">
                          <button
                            type="button"
                            onClick={() => handleRemovePathway(pathway.id)}
                            disabled={loading}
                            className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50 text-sm font-medium"
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {defaultPathways.length === 0 && (
              <p className="text-sm text-gray-500 italic">
                No default pathways added yet. Add one to get started.
              </p>
            )}
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium"
            >
              {loading ? "Creating..." : "Create Scenathon"}
            </button>
            <button
              type="reset"
              disabled={loading}
              onClick={() => {
                setFormData({
                  name: "",
                  notification_email: "",
                  status: "active",
                  allow_trade: false,
                  year: new Date().getFullYear(),
                  register: "active",
                  type: "",
                });
                setDefaultPathways([]);
                setNewPathway({
                  defaultname: "",
                  deadline: "",
                });
              }}
              className="flex-1 px-4 py-2 bg-gray-400 text-white rounded-lg hover:bg-gray-500 disabled:opacity-50 font-medium"
            >
              Clear
            </button>
          </div>
        </form>
    </div>
  );
}
